/**
 * Typy, na kterych se shodne server a web. Jediny zdroj pravdy o tvaru API.
 *
 * Klicovy model: polozka NENI dvojice "anglicky vyraz + cesky preklad".
 * Je to KONCEPT (jedna myslenka) s variantami v N jazycich. Diky tomu jde
 * zkouset libovolny smer: cs->en, en->cs, en->de, de->es...
 */

import type { CardState, Rating } from './fsrs.ts';

// ---------------------------------------------------------------------------
// Jazyky
// ---------------------------------------------------------------------------

export type Lang = 'cs' | 'en' | 'de' | 'es' | 'it';

export const LANGS: readonly Lang[] = ['cs', 'en', 'de', 'es', 'it'];

export const LANG_NAMES: Record<Lang, string> = {
  cs: 'čeština',
  en: 'angličtina',
  de: 'němčina',
  es: 'španělština',
  it: 'italština',
};

/** Kratky kod do UI (vlajecky nepouzivame - jazyk != stat). */
export const LANG_CODES: Record<Lang, string> = {
  cs: 'CS',
  en: 'EN',
  de: 'DE',
  es: 'ES',
  it: 'IT',
};

export function isLang(value: string): value is Lang {
  return (LANGS as readonly string[]).includes(value);
}

/**
 * Smer zkouseni: vidim vyraz ve `from`, vybavuji si ho v `to`.
 * Serializuje se jako "en>cs" - kratke a citelne v URL i v DB.
 */
export interface Direction {
  from: Lang;
  to: Lang;
}

export const directionKey = (d: Direction): string => `${d.from}>${d.to}`;

export function parseDirection(key: string): Direction | null {
  const [from, to] = key.split('>');
  if (!from || !to || !isLang(from) || !isLang(to) || from === to) return null;
  return { from, to };
}

export const flipDirection = (d: Direction): Direction => ({ from: d.to, to: d.from });

// ---------------------------------------------------------------------------
// Obsah
// ---------------------------------------------------------------------------

export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export const CEFR_LEVELS: readonly CefrLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export type PartOfSpeech =
  | 'noun'
  | 'verb'
  | 'adjective'
  | 'adverb'
  | 'phrase'
  | 'idiom'
  | 'phrasal_verb'
  | 'preposition'
  | 'interjection'
  | 'other';

export type Register = 'neutral' | 'informal' | 'formal' | 'slang' | 'vulgar';

/** Jak se dany koncept rekne v jednom konkretnim jazyce. */
export interface Term {
  lang: Lang;
  term: string;
  /** Dalsi mozne preklady/synonyma. */
  variants: string[];
  ipa: string | null;
  pos: PartOfSpeech;
  register: Register;
  /** Poznamka k pouziti v tomto jazyce - past, predlozka, rozdil BrE/AmE. */
  note: string | null;
  collocations: string[];
}

/** Jedna prikladova veta prelozena do vice jazyku. Klic = kod jazyka. */
export type Example = Partial<Record<Lang, string>> & {
  /** Volitelny kontext, napr. "hospoda", "pracovni e-mail". */
  context?: string;
};

/** Koncept = jedna myslenka napric jazyky. */
export interface Concept {
  id: string;
  /** Stabilni klic, napr. "hit-it-off". Slouzi k parovani pri importu. */
  key: string;
  level: CefrLevel | null;
  frequencyRank: number | null;
  topics: string[];
  /** Varianty po jazycich. Nemusi byt vyplnene vsechny. */
  terms: Partial<Record<Lang, Term>>;
  examples: Example[];
  /** Ve kterych jazycich je koncept k dispozici. */
  langs: Lang[];
}

/** Uzivatelska prioritizace konceptu - "co uz umim, at pada niz". */
export interface ConceptPrefs {
  priority: number;
  known: boolean;
  suppressed: boolean;
}

// ---------------------------------------------------------------------------
// Zkouseni
// ---------------------------------------------------------------------------

export interface ReviewCard {
  cardId: string;
  direction: Direction;
  state: CardState;
  reps: number;
  concept: Concept;
  prefs: ConceptPrefs;
  intervalPreview: Record<Rating, number>;
}

export interface ReviewQueue {
  cards: ReviewCard[];
  dueCount: number;
  newRemaining: number;
  generatedAt: string;
}

export interface AnswerInput {
  clientId: string;
  cardId: string;
  rating: Rating;
  answeredAt: string;
  elapsedMs?: number;
  typedAnswer?: string;
}

export interface AnswerResult {
  cardId: string;
  clientId: string;
  duplicate: boolean;
  intervalDays: number;
  dueAt: string;
  state: CardState;
}

export interface Deck {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  conceptCount: number;
  /** Ve kterych jazycich ma balicek obsah a kolik konceptu v kazdem. */
  langCoverage: Partial<Record<Lang, number>>;
  active: boolean;
}

export interface UserSettings {
  newPerDay: number;
  dailyGoal: number;
  requestRetention: number;
  /**
   * Aktivni smery zkouseni, napr. ["en>cs", "cs>en", "en>de"].
   * Uzivatel si je libovolne sklada a otaci.
   */
  directions: string[];
  autoplayAudio: boolean;
  /** Ktere dalsi jazyky ukazat na odkryte karte "vedle sebe". */
  showLangs: Lang[];
}

export interface Stats {
  streakDays: number;
  reviewedToday: number;
  dailyGoal: number;
  dueNow: number;
  dueTomorrow: number;
  totalConcepts: number;
  byState: Record<CardState, number>;
  /** Rozpad karet podle smeru zkouseni. */
  byDirection: { direction: string; total: number; due: number }[];
  retention30d: number | null;
  history: { date: string; count: number }[];
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
}

/** Jedno vytěžené slovo z článku, i s větou ze zdroje. */
export interface ExtractCandidate {
  sourceTerm: string;
  targetTerm: string;
  context: string;
  contextTranslated: string | null;
  alreadyHave: boolean;
  /** Jen z AI: dalsi vyznamy, poznamka k pouziti a odhad urovne. */
  variants?: string[];
  note?: string | null;
  level?: CefrLevel | null;
}

export interface ExtractPreview {
  scanned: number;
  alreadyHave: number;
  items: ExtractCandidate[];
  /** Odkud pokracovat dalsi davkou novych slov; `null` = dal uz nic neni. */
  nextOffset: number | null;
  /** Kolik novych slov zbyva za touto davkou. */
  remaining: number;
  /** Prekladac vycerpal denni limit - davka je neuplna, pokracovat jde pozdeji. */
  quotaHit: boolean;
  /**
   * `basic` = slova po 30 z MyMemory, posun se pocita ve slovech (pridana slova
   * z dalsi davky vypadnou); `ai` = Gemini po castech textu, posun = cislo casti.
   */
  mode: 'ai' | 'basic';
}

export interface ExtractCaptureResult {
  added: number;
  existed: number;
  failed: number;
}

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}

/** Verejny heartbeat + identita nasazeni (git hash z git archive). */
export interface HealthInfo {
  ok: boolean;
  ts: string;
  version: string;
  git: string;
  built: string;
}
